import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { getApiBase, getAuthHeaders } from '../../utils/api';

const EmailParsingSettings = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Settings state
  const [settings, setSettings] = useState({
    preferred_parser: 'awardwallet_primary',
    enable_fallback: true,
    enable_field_merge: true,
    document_type_overrides: {},
    log_priority_decisions: true
  });

  // Priority options from backend
  const [priorityOptions, setPriorityOptions] = useState([]);
  const [documentTypes, setDocumentTypes] = useState([]);

  // For adding document type overrides
  const [newOverrideType, setNewOverrideType] = useState('');
  const [newOverridePriority, setNewOverridePriority] = useState('');

  useEffect(() => {
    if (user?.role === 'superadmin') {
      loadSettings();
      loadPriorityOptions();
    }
  }, [user]);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();
      const response = await axios.get(`${apiBase}/admin/email-parsing/settings`, { headers });
      if (response.data.settings) {
        setSettings(prev => ({
          ...prev,
          ...response.data.settings
        }));
      }
    } catch (err) {
      console.error('Error loading settings:', err);
      setError('Failed to load settings: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const loadPriorityOptions = async () => {
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();
      const response = await axios.get(`${apiBase}/admin/email-parsing/priority-options`, { headers });
      if (response.data.priority_options) {
        setPriorityOptions(response.data.priority_options);
      }
      if (response.data.document_types) {
        setDocumentTypes(response.data.document_types);
      }
    } catch (err) {
      console.error('Error loading priority options:', err);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccessMessage('');
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();
      await axios.put(`${apiBase}/admin/email-parsing/settings`, settings, { headers });
      setSuccessMessage('Settings saved successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error saving settings:', err);
      setError('Failed to save settings: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  const addOverride = () => {
    if (newOverrideType && newOverridePriority) {
      setSettings(prev => ({
        ...prev,
        document_type_overrides: {
          ...prev.document_type_overrides,
          [newOverrideType]: newOverridePriority
        }
      }));
      setNewOverrideType('');
      setNewOverridePriority('');
    }
  };

  const removeOverride = (docType) => {
    setSettings(prev => {
      const newOverrides = { ...prev.document_type_overrides };
      delete newOverrides[docType];
      return {
        ...prev,
        document_type_overrides: newOverrides
      };
    });
  };

  const getPriorityLabel = (value) => {
    const option = priorityOptions.find(o => o.value === value);
    return option ? option.label : value;
  };

  const getPriorityDescription = (value) => {
    const option = priorityOptions.find(o => o.value === value);
    return option ? option.description : '';
  };

  if (user?.role !== 'superadmin') {
    return (
      <div className="p-6 bg-red-50 text-red-600 rounded-lg">
        Access denied. Superadmin privileges required.
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-600">Loading settings...</span>
      </div>
    );
  }

  // Available document types for overrides (exclude already overridden ones)
  const availableDocTypes = documentTypes.filter(
    type => !Object.keys(settings.document_type_overrides).includes(type)
  );

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Email Parsing Priority Settings</h1>
        <p className="mt-2 text-gray-600">
          Configure which parsing service (AI or AwardWallet) takes priority when processing travel confirmation emails.
          Both parsers run in parallel, and these settings determine which results are used.
        </p>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {error}
        </div>
      )}

      {successMessage && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700">
          {successMessage}
        </div>
      )}

      {/* Primary Parser Selection */}
      <div className="mb-6 p-4 bg-white border rounded-lg shadow-sm">
        <h2 className="text-lg font-semibold mb-4">Primary Parser</h2>
        <p className="text-sm text-gray-600 mb-4">
          Select which parsing service should be preferred when both successfully parse an email.
        </p>
        <div className="space-y-3">
          {priorityOptions.map(option => (
            <label
              key={option.value}
              className={`flex items-start p-3 border rounded-lg cursor-pointer transition-colors ${
                settings.preferred_parser === option.value
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <input
                type="radio"
                name="preferred_parser"
                value={option.value}
                checked={settings.preferred_parser === option.value}
                onChange={(e) => setSettings(prev => ({ ...prev, preferred_parser: e.target.value }))}
                className="mt-1 w-4 h-4 text-blue-600"
              />
              <div className="ml-3">
                <span className="font-medium text-gray-900">{option.label}</span>
                <p className="text-sm text-gray-500">{option.description}</p>
              </div>
            </label>
          ))}
        </div>
      </div>

      {/* Fallback Behavior */}
      <div className="mb-6 p-4 bg-white border rounded-lg shadow-sm">
        <h2 className="text-lg font-semibold mb-4">Fallback Behavior</h2>
        <div className="space-y-4">
          <label className="flex items-center justify-between">
            <div>
              <span className="font-medium">Enable Fallback</span>
              <p className="text-sm text-gray-500">
                If the primary parser fails or returns no results, use the secondary parser's results
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={settings.enable_fallback}
                onChange={(e) => setSettings(prev => ({ ...prev, enable_fallback: e.target.checked }))}
                className="sr-only peer"
                disabled={settings.preferred_parser === 'ai_only' || settings.preferred_parser === 'awardwallet_only'}
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600 peer-disabled:opacity-50"></div>
            </label>
          </label>

          <label className="flex items-center justify-between">
            <div>
              <span className="font-medium">Enable Field Merge</span>
              <p className="text-sm text-gray-500">
                Fill in missing critical fields from the secondary parser when the primary is missing data
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={settings.enable_field_merge}
                onChange={(e) => setSettings(prev => ({ ...prev, enable_field_merge: e.target.checked }))}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </label>

          <label className="flex items-center justify-between">
            <div>
              <span className="font-medium">Log Priority Decisions</span>
              <p className="text-sm text-gray-500">
                Log which parser was selected and why for debugging purposes
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={settings.log_priority_decisions}
                onChange={(e) => setSettings(prev => ({ ...prev, log_priority_decisions: e.target.checked }))}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </label>
        </div>

        {(settings.preferred_parser === 'ai_only' || settings.preferred_parser === 'awardwallet_only') && (
          <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <p className="text-sm text-amber-700">
              <strong>Note:</strong> Fallback is not applicable when using "{getPriorityLabel(settings.preferred_parser)}" mode.
            </p>
          </div>
        )}
      </div>

      {/* Document Type Overrides */}
      <div className="mb-6 p-4 bg-white border rounded-lg shadow-sm">
        <h2 className="text-lg font-semibold mb-4">Document Type Overrides</h2>
        <p className="text-sm text-gray-600 mb-4">
          Override the default priority for specific document types. For example, use AI for car rentals
          but AwardWallet for flights.
        </p>

        {/* Existing overrides */}
        {Object.keys(settings.document_type_overrides).length > 0 ? (
          <div className="mb-4">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2 px-3 font-medium text-gray-700">Document Type</th>
                  <th className="text-left py-2 px-3 font-medium text-gray-700">Priority</th>
                  <th className="text-right py-2 px-3 font-medium text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(settings.document_type_overrides).map(([docType, priority]) => (
                  <tr key={docType} className="border-b">
                    <td className="py-2 px-3">
                      <span className="inline-block px-2 py-1 bg-gray-100 rounded text-sm font-medium">
                        {docType}
                      </span>
                    </td>
                    <td className="py-2 px-3">
                      <span className="text-sm">{getPriorityLabel(priority)}</span>
                    </td>
                    <td className="py-2 px-3 text-right">
                      <button
                        onClick={() => removeOverride(docType)}
                        className="text-red-600 hover:text-red-800 text-sm"
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-sm text-gray-500 mb-4 italic">
            No document type overrides configured. All document types use the global priority setting.
          </p>
        )}

        {/* Add new override */}
        {availableDocTypes.length > 0 && (
          <div className="flex items-end gap-3">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">Document Type</label>
              <select
                value={newOverrideType}
                onChange={(e) => setNewOverrideType(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Select type...</option>
                {availableDocTypes.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
              <select
                value={newOverridePriority}
                onChange={(e) => setNewOverridePriority(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Select priority...</option>
                {priorityOptions.map(option => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>
            <button
              onClick={addOverride}
              disabled={!newOverrideType || !newOverridePriority}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              Add
            </button>
          </div>
        )}
      </div>

      {/* Priority Flow Diagram */}
      <div className="mb-6 p-4 bg-gray-50 border rounded-lg">
        <h2 className="text-lg font-semibold mb-4">Current Priority Flow</h2>
        <div className="flex items-center justify-center space-x-4 text-sm">
          <div className={`px-4 py-2 rounded-lg border-2 ${
            settings.preferred_parser.includes('ai') && !settings.preferred_parser.includes('awardwallet')
              ? 'border-blue-500 bg-blue-100'
              : settings.preferred_parser === 'best_result'
                ? 'border-purple-500 bg-purple-100'
                : 'border-gray-300 bg-white'
          }`}>
            <div className="font-semibold">AI Parser</div>
            <div className="text-xs text-gray-500">
              {settings.preferred_parser === 'ai_primary' && '(Primary)'}
              {settings.preferred_parser === 'ai_only' && '(Only)'}
              {settings.preferred_parser === 'awardwallet_primary' && settings.enable_fallback && '(Fallback)'}
              {settings.preferred_parser === 'best_result' && '(Compared)'}
            </div>
          </div>

          <div className="text-gray-400">
            {settings.preferred_parser === 'best_result' ? (
              <span>vs</span>
            ) : settings.enable_fallback && !settings.preferred_parser.includes('only') ? (
              <span>&harr;</span>
            ) : (
              <span></span>
            )}
          </div>

          <div className={`px-4 py-2 rounded-lg border-2 ${
            settings.preferred_parser.includes('awardwallet') && !settings.preferred_parser.includes('ai')
              ? 'border-green-500 bg-green-100'
              : settings.preferred_parser === 'best_result'
                ? 'border-purple-500 bg-purple-100'
                : 'border-gray-300 bg-white'
          }`}>
            <div className="font-semibold">AwardWallet</div>
            <div className="text-xs text-gray-500">
              {settings.preferred_parser === 'awardwallet_primary' && '(Primary)'}
              {settings.preferred_parser === 'awardwallet_only' && '(Only)'}
              {settings.preferred_parser === 'ai_primary' && settings.enable_fallback && '(Fallback)'}
              {settings.preferred_parser === 'best_result' && '(Compared)'}
            </div>
          </div>
        </div>

        {settings.enable_field_merge && !settings.preferred_parser.includes('only') && (
          <p className="text-center text-xs text-gray-500 mt-3">
            + Missing fields merged from secondary parser
          </p>
        )}
      </div>

      {/* Save Button */}
      <div className="flex justify-end space-x-4">
        <button
          onClick={loadSettings}
          disabled={saving}
          className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
        >
          Reset
        </button>
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-400 flex items-center"
        >
          {saving ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Saving...
            </>
          ) : (
            'Save Settings'
          )}
        </button>
      </div>
    </div>
  );
};

export default EmailParsingSettings;
